import { Capacitor } from "@capacitor/core";
import type { ConvexReactClient } from "convex/react";

import { mutationRef } from "../convex/refs";
import {
  getNotificationPermissionStatus,
  type NotificationPermissionResult,
} from "./notifications";

type UpdateNotificationPermissionArgs = {
  anonymousDeviceId: string;
  notificationPermissionStatus: NotificationPermissionResult;
  platform: "ios" | "android" | "web";
};

type UpdateNotificationPermissionResult = {
  updated: boolean;
  created: boolean;
};

const updateNotificationPermissionRef = mutationRef<
  UpdateNotificationPermissionArgs,
  UpdateNotificationPermissionResult
>("devices:updateNotificationPermission");

function nativePlatform(): "ios" | "android" | "web" {
  return Capacitor.getPlatform() as "ios" | "android" | "web";
}

/** Web / missing OneSignal config: skip Convex writes; field stays undefined. */
export async function persistNotificationPermissionToConvex(
  convex: ConvexReactClient,
  anonymousDeviceId: string,
  status: NotificationPermissionResult,
): Promise<void> {
  if (status === "unsupported") return;

  try {
    await convex.mutation(updateNotificationPermissionRef, {
      anonymousDeviceId,
      notificationPermissionStatus: status,
      platform: nativePlatform(),
    });
  } catch (error) {
    console.warn("Failed to persist notification permission", error);
  }
}

/** Read native permission (no prompt) and persist to Convex. */
export async function syncNotificationPermissionToConvex(
  convex: ConvexReactClient,
  anonymousDeviceId: string,
): Promise<NotificationPermissionResult> {
  const status = await getNotificationPermissionStatus();
  await persistNotificationPermissionToConvex(convex, anonymousDeviceId, status);
  return status;
}
